import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import DOMPurify from "dompurify";
import useScrollToTop from "../hooks/useScrollToTop";
import { getNews } from "../services/newsServices";
import { LanguageContext } from "../contexts/LanguageContext";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const absUrl = (u) => (u ? (isExternalURL(u) ? u : `${BASE_URL}${u}`) : "");

// extrait texte propre depuis HTML (sans casser les balises)
const htmlToText = (html = "") => {
  const safe = DOMPurify.sanitize(String(html || ""));
  try {
    const doc = new DOMParser().parseFromString(safe, "text/html");
    return (doc.body?.textContent || "").replace(/\s+/g, " ").trim();
  } catch {
    return String(html || "").replace(/\s+/g, " ").trim();
  }
};

const clampText = (s, n = 180) => {
  const str = String(s || "");
  if (str.length <= n) return str;
  return str.slice(0, n).trim() + "…";
};

const NewsPage = () => {
  useScrollToTop();

  const { language } = useContext(LanguageContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [imgIdxById, setImgIdxById] = useState({}); // index image par carte

  // modal article
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // zoom image (optionnel)
  const [zoomImage, setZoomImage] = useState(null);

  const getLocalizedKey = useCallback(
    (key) => `${key}_${String(language || "FR").toLowerCase()}`,
    [language]
  );

  const getFallback = useCallback(
    (type) => {
      if (language === "FR") return type === "title" ? "Titre indisponible" : "Texte indisponible";
      return type === "title" ? "Title unavailable" : "Text unavailable";
    },
    [language]
  );

  const getImages = useCallback((article) => {
    const arr = [article?.image_url, article?.image_url2].filter(Boolean).map(absUrl);
    return arr;
  }, []);

  // Fetch news
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await getNews();
        const active = (response || []).filter((item) => item?.is_active);

        setNews(active);

        // init indexes
        const initial = active.reduce((acc, a) => {
          acc[a.id] = 0;
          return acc;
        }, {});
        setImgIdxById(initial);
      } catch (error) {
        console.error("Erreur lors de la récupération des actualités :", error);
        setNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Auto-slide (soft) uniquement si 2 images
  useEffect(() => {
    if (!news.length) return;

    const interval = setInterval(() => {
      setImgIdxById((prev) => {
        const next = { ...prev };

        for (const article of news) {
          const imgs = getImages(article);
          if (imgs.length <= 1) continue;

          const cur = Number(next[article.id] || 0);
          next[article.id] = (cur + 1) % imgs.length;
        }
        return next;
      });
    }, 6500);

    return () => clearInterval(interval);
  }, [news, getImages]);

  // open/close modal
  const openModal = useCallback((article) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedArticle(null);
    setZoomImage(null);
  }, []);

  // scroll lock + keyboard modal
  useEffect(() => {
    if (!isModalOpen) return;

    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isModalOpen, closeModal]);

  // UI styles
  const pageWrap = "relative min-h-screen pb-12";
  const sectionWrap = "relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
  const card =
    "rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 " +
    "bg-[#E8BB89]/80 dark:bg-white/5 shadow-sm backdrop-blur-sm";
  const softText = "text-gray-900 dark:text-white";
  const mutedText = "text-gray-800 dark:text-gray-300";
  const buttonPrimary =
    "inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99]";

  const heroTitle = useMemo(() => t("homepage.lastnews") || "Actualités", [t]);

  const isEmpty = !loading && news.length === 0;

  if (loading) {
    return (
      <p className="text-center text-gray-600 dark:text-gray-300 py-12">
        {t("news.loading")}
      </p>
    );
  }

  return (
    <div className={pageWrap}>
      {/* Décor page */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-[60px] left-0 w-full h-[34vh] bg-cover bg-no-repeat bg-bottom opacity-60 dark:opacity-25"
          style={{ backgroundImage: "url('/images/fond_sakura.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 to-transparent dark:from-black/60" />
      </div>

      {/* HERO */}
      <section className={`${sectionWrap} pt-24 sm:pt-28`}>
        <div className="text-center">
          <div
            className="
              inline-flex items-center gap-2 rounded-full px-4 py-2
              bg-black/5 dark:bg-white/10
              border border-black/10 dark:border-white/10
              backdrop-blur-sm
            "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {language === "FR" ? "Actualités" : "News"}
            </span>
          </div>

          <h1 className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-satoshi font-extrabold tracking-tight ${softText}`}>
            {heroTitle}
          </h1>

          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
          </div>

          <p className={`mt-4 max-w-3xl mx-auto text-sm sm:text-base ${mutedText}`}>
            {language === "FR"
              ? "Découvrez les dernières actualités, événements et informations importantes."
              : "Discover the latest news, events, and important updates."}
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className={`${sectionWrap} mt-10`}>
        {isEmpty ? (
          <div className={`${card} p-8 text-center`}>
            <p className={`${mutedText}`}>
              {t("news.contentUnavailable") || (language === "FR" ? "Aucune actualité disponible." : "No news available.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {news.map((article) => {
              const images = getImages(article);
              const idx = Number(imgIdxById[article.id] || 0);
              const activeImage = images[idx] || images[0];

              const title =
                article?.[getLocalizedKey("title")] || t("news.titleUnavailable") || getFallback("title");

              const contentHtml = article?.[getLocalizedKey("content")] || "";
              const snippet = clampText(htmlToText(contentHtml), 190);

              const canNav = images.length > 1;

              const prev = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setImgIdxById((s) => ({
                  ...s,
                  [article.id]: (Number(s[article.id] || 0) - 1 + images.length) % images.length,
                }));
              };

              const next = (e) => {
                e.preventDefault();
                e.stopPropagation();
                setImgIdxById((s) => ({
                  ...s,
                  [article.id]: (Number(s[article.id] || 0) + 1) % images.length,
                }));
              };

              return (
                <button
                  key={article.id}
                  onClick={() => openModal(article)}
                  className={`
                    ${card}
                    text-left w-full
                    transition-transform duration-300
                    hover:-translate-y-1
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                  `}
                >
                  {/* Image */}
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    {activeImage ? (
                      <img
                        src={activeImage}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out hover:scale-[1.03]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-black/5 dark:bg-white/5" />
                    )}

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

                    {/* Arrows si 2 images */}
                    {canNav && (
                      <>
                        <div className="absolute inset-x-0 top-0 p-3 flex items-center justify-between">
                          <span className="text-xs text-white/85 bg-black/30 px-2 py-1 rounded-full border border-white/10 backdrop-blur">
                            {idx + 1}/{images.length}
                          </span>
                        </div>

                        <button
                          onClick={prev}
                          className="
                            absolute left-3 top-1/2 -translate-y-1/2
                            w-10 h-10 rounded-2xl
                            bg-white/15 hover:bg-white/20
                            border border-white/15 backdrop-blur
                            text-white transition
                          "
                          aria-label="Précédent"
                          title="Précédent"
                        >
                          <FaChevronLeft className="mx-auto" />
                        </button>

                        <button
                          onClick={next}
                          className="
                            absolute right-3 top-1/2 -translate-y-1/2
                            w-10 h-10 rounded-2xl
                            bg-white/15 hover:bg-white/20
                            border border-white/15 backdrop-blur
                            text-white transition
                          "
                          aria-label="Suivant"
                          title="Suivant"
                        >
                          <FaChevronRight className="mx-auto" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Text */}
                  <div className="p-5 sm:p-6">
                    <h2 className={`text-lg sm:text-xl font-satoshi font-extrabold tracking-tight ${softText}`}>
                      {title}
                    </h2>

                    <p className={`mt-3 text-sm leading-relaxed ${mutedText}`}>
                      {snippet}
                    </p>

                    <div className="mt-5 flex items-center gap-3">
                      <span
                        className="
                          inline-flex items-center justify-center rounded-2xl
                          bg-primary px-4 py-2 text-white text-sm font-semibold
                          shadow hover:opacity-95 transition
                        "
                      >
                        {t("news.readMore")}
                      </span>

                      <span className="text-xs text-gray-700/80 dark:text-gray-300/80">
                        {language === "FR" ? "Cliquer pour lire" : "Click to read"}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* MODAL ARTICLE */}
      {isModalOpen && selectedArticle && (
        <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal}>
          <div
            className="
              relative w-full max-w-5xl
              rounded-3xl overflow-hidden
              border border-white/10
              bg-white/95 dark:bg-neutral-950/95
              shadow-2xl
            "
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-4 sm:p-6 border-b border-black/10 dark:border-white/10">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-black/5 dark:bg-white/10 border border-black/10 dark:border-white/10">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    {language === "FR" ? "Actualité" : "News"}
                  </span>
                </div>

                <h2 className="mt-3 text-2xl sm:text-3xl font-satoshi font-extrabold tracking-tight text-primary">
                  {selectedArticle[getLocalizedKey("title")] || t("news.titleUnavailable") || getFallback("title")}
                </h2>
              </div>

              <button
                onClick={closeModal}
                className="
                  w-11 h-11 rounded-2xl flex items-center justify-center
                  bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15
                  border border-black/10 dark:border-white/10
                  transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                "
                aria-label={t("news.close")}
                title={t("news.close")}
              >
                <FaTimes className="text-gray-900 dark:text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 max-h-[78vh] overflow-y-auto">
              {/* Images (carousel simple) */}
              {(() => {
                const images = getImages(selectedArticle);
                if (!images.length) return null;

                // on réutilise imgIdxById mais sur l’article sélectionné
                const cur = Number(imgIdxById[selectedArticle.id] || 0);
                const active = images[cur] || images[0];
                const canNav = images.length > 1;

                const prev = () =>
                  setImgIdxById((s) => ({
                    ...s,
                    [selectedArticle.id]: (Number(s[selectedArticle.id] || 0) - 1 + images.length) % images.length,
                  }));

                const next = () =>
                  setImgIdxById((s) => ({
                    ...s,
                    [selectedArticle.id]: (Number(s[selectedArticle.id] || 0) + 1) % images.length,
                  }));

                return (
                  <div className="mb-6">
                    <div className="relative w-full aspect-[16/9] overflow-hidden rounded-3xl border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5">
                      <img
                        src={active}
                        alt="illustration"
                        className="h-full w-full object-contain cursor-zoom-in"
                        onClick={() => setZoomImage(active)}
                        loading="lazy"
                      />

                      {canNav && (
                        <>
                          <div className="absolute top-3 left-3 text-xs text-white/90 bg-black/35 px-2 py-1 rounded-full border border-white/10 backdrop-blur">
                            {cur + 1}/{images.length}
                          </div>

                          <button
                            type="button"
                            onClick={prev}
                            className="
                              absolute left-3 top-1/2 -translate-y-1/2
                              w-11 h-11 rounded-2xl
                              bg-white/15 hover:bg-white/20
                              border border-white/15 backdrop-blur
                              text-white transition
                            "
                            aria-label="Précédent"
                            title="Précédent"
                          >
                            <FaChevronLeft className="mx-auto" />
                          </button>

                          <button
                            type="button"
                            onClick={next}
                            className="
                              absolute right-3 top-1/2 -translate-y-1/2
                              w-11 h-11 rounded-2xl
                              bg-white/15 hover:bg-white/20
                              border border-white/15 backdrop-blur
                              text-white transition
                            "
                            aria-label="Suivant"
                            title="Suivant"
                          >
                            <FaChevronRight className="mx-auto" />
                          </button>
                        </>
                      )}
                    </div>

                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                      {language === "FR" ? "Clique sur l'image pour agrandir." : "Click the image to zoom."}
                    </div>
                  </div>
                );
              })()}

              {/* Texte complet */}
              <div
                className="
                  prose prose-neutral max-w-none
                  dark:prose-invert
                  prose-headings:font-satoshi
                  prose-p:leading-relaxed
                  text-gray-800 dark:text-gray-300
                "
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    selectedArticle[getLocalizedKey("content")] ||
                      `<p>${t("news.contentUnavailable") || getFallback("text")}</p>`
                  ),
                }}
              />
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-6 border-t border-black/10 dark:border-white/10 flex justify-center">
              <button onClick={closeModal} className={buttonPrimary}>
                {t("news.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ZOOM IMAGE */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <img
            src={zoomImage}
            alt="Agrandissement"
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-2xl bg-black/20"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="
              absolute top-5 right-5
              w-11 h-11 rounded-2xl
              bg-white/10 hover:bg-white/15
              border border-white/10
              text-white text-2xl
            "
            onClick={() => setZoomImage(null)}
            aria-label="Fermer"
            title="Fermer"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
